import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { useUserRoles } from "@/lib/auth-store";
import { listarUsuariosAdmin } from "@/features/admin-usuarios/services/usuarios-service";
import type { UsuarioAdmin } from "@/lib/types";

/**
 * Devuelve un map id (lowercase) → UsuarioAdmin para enriquecer las
 * filas de auditoría de documentos. Solo el admin tiene permiso de
 * leer el listado de usuarios · el Auditor solo verá los ids.
 *
 * El `enabled: esAdmin` evita el 403 cuando un Auditor entra a la
 * página.
 */
export function useDirectorioUsuarios(): Map<string, UsuarioAdmin> {
  const roles = useUserRoles();
  const esAdmin = roles.includes("Administrador");

  const usuarios = useQuery({
    queryKey: [...qk.adminUsuarios, "directorio"] as const,
    // El listado admin ahora es paginado (15/pág) · para el directorio se
    // recorren todas las páginas (dataset chico · mismo patrón del export).
    queryFn: async () => {
      const todos: UsuarioAdmin[] = [];
      for (let page = 1; ; page++) {
        const pagina = await listarUsuariosAdmin({ page, pageSize: 100 });
        todos.push(...pagina.items);
        if (todos.length >= pagina.total || pagina.items.length === 0) break;
      }
      return todos;
    },
    enabled: esAdmin,
  });

  return useMemo(() => {
    const m = new Map<string, UsuarioAdmin>();
    for (const u of usuarios.data ?? []) m.set(u.id.toLowerCase(), u);
    return m;
  }, [usuarios.data]);
}
